import { Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GAMES } from '../games'
import { usePracticeRun } from '../hooks/usePracticeRun'
import { practiceApi } from '../api/practice.api'
import { describeCorrectValue, itemPrompt } from '../utils'
import type { Answer, ItemReportReason, PracticeRun } from '../types'
import { GameHud } from './GameHud'
import { ResultScreen } from './ResultScreen'
import type { ResultScreenItemDetail } from './ResultScreen'
import { RoundErrorBoundary } from './RoundErrorBoundary'

export interface GameShellProps {
  /** Partie déjà démarrée (`usePracticeRun().startRun`) — le choix du jeu et des topics reste à la page hôte. */
  readonly run: PracticeRun
  /** Appelé après confirmation d'abandon ; la navigation reste à la charge de la page hôte. */
  readonly onAbandon?: () => void
  readonly onExit?: () => void
}

type Phase = 'playing' | 'confirm_abandon' | 'finishing' | 'finished' | 'abandoned' | 'error'

/**
 * Orchestre une partie : manche courante, progression, abandon, passage à
 * l'écran de fin. Ne connaît rien du contenu d'un item — c'est le rôle du
 * `Round` de la mécanique, résolu depuis `GAMES[run.game]` — seulement de la
 * mécanique du déroulé.
 *
 * Retour immédiat vs différé (SPEC §8) : en mode `solo`, c'est au `Round` de
 * retarder son appel à `onAnswer` le temps d'afficher sa propre correction —
 * cette coquille avance à la manche suivante dès que `onAnswer` est appelé,
 * quel que soit `run.mode`. En `daily`/`duel`, `Round` ne peut de toute façon
 * pas afficher de correction immédiate : l'item est servi sans réponse
 * (`ServedItem`).
 */
export function GameShell({ run, onAbandon, onExit }: GameShellProps) {
  const { t } = useTranslation()
  const template = GAMES[run.game]
  const [phase, setPhase] = useState<Phase>('playing')
  const [roundIndex, setRoundIndex] = useState(0)
  const [liveScore, setLiveScore] = useState(0)
  const [answers, setAnswers] = useState<Answer[]>([])
  const [runDurationMs, setRunDurationMs] = useState(0)
  const { submitAnswers, finishRun, result, isFinishing } = usePracticeRun()

  const currentItem = run.items[roundIndex]
  const totalRounds = run.items.length

  // Départ de la partie, pour la durée réelle du run (SPEC §6.6 `duration_ms`)
  // — jamais la somme des latences par item, qui diverge dès qu'il y a une
  // pause ou une manche non répondue. Initialisé à 0 (valeur pure) plutôt
  // qu'à `performance.now()` directement — appeler une fonction impure
  // pendant le rendu est interdit (`react-hooks/purity`) — et posé par
  // l'effet ci-dessous dès le montage.
  const startedAtRef = useRef(0)
  useEffect(() => {
    startedAtRef.current = performance.now()
  }, [])
  const pendingAnswersRef = useRef<Answer[]>([])

  const closeRun = async (finalAnswers: Answer[]) => {
    pendingAnswersRef.current = finalAnswers
    setPhase('finishing')
    setRunDurationMs(performance.now() - startedAtRef.current)

    try {
      await submitAnswers(run.id, finalAnswers)
      await finishRun(run.id)
      setPhase('finished')
    } catch {
      // Réponses et score restent en mémoire (`pendingAnswersRef`) : le
      // bouton « Réessayer » de l'écran d'erreur rejoue le même envoi, sans
      // faire perdre la partie à une simple coupure réseau.
      setPhase('error')
    }
  }

  const handleAnswer = (value: unknown, ms: number, isCorrect?: boolean) => {
    if (!currentItem) return

    const nextAnswers = [...answers, { item_id: currentItem.id, value, ms }]
    setAnswers(nextAnswers)
    if (isCorrect) setLiveScore((score) => score + 1)

    if (roundIndex + 1 >= totalRounds) {
      void closeRun(nextAnswers)
    } else {
      setRoundIndex((index) => index + 1)
    }
  }

  const handleTimeUp = () => {
    void closeRun(answers)
  }

  const handleRetryClose = () => {
    void closeRun(pendingAnswersRef.current)
  }

  const handleReportItem = (itemId: string, reason: ItemReportReason) => practiceApi.reportItem(itemId, { reason })

  if (phase === 'abandoned') {
    return (
      <section aria-label={t('practice.shell.abandoned_title')} className="flex flex-col gap-3 px-4 py-6 text-center">
        <p className="font-display text-lg font-semibold text-on-surface">{t('practice.shell.abandoned_title')}</p>
        {onExit && (
          <button
            type="button"
            onClick={onExit}
            className="min-h-[44px] self-center rounded-full bg-surface-low px-4 text-sm font-medium text-on-surface"
          >
            {t('practice.result.exit')}
          </button>
        )}
      </section>
    )
  }

  if (phase === 'error') {
    return (
      <section role="alert" className="flex flex-col gap-3 px-4 py-6 text-center">
        <p className="font-display text-lg font-semibold text-on-surface">{t('practice.shell.error_title')}</p>
        <p className="text-sm text-on-surface-variant">{t('practice.shell.error_body')}</p>
        <button
          type="button"
          onClick={handleRetryClose}
          className="min-h-[44px] self-center rounded-full bg-primary px-4 text-sm font-semibold text-white"
        >
          {t('practice.shell.retry')}
        </button>
      </section>
    )
  }

  if (phase === 'finished' && result) {
    const items: ResultScreenItemDetail[] = result.corrections.map((correction) => {
      const item = run.items.find((candidate) => candidate.id === correction.item_id)
      return {
        item_id: correction.item_id,
        prompt: item ? itemPrompt(item) : '',
        correct_answer: item ? describeCorrectValue(item, correction.correct_value, t) : '',
        is_correct: correction.is_correct,
        explanation: correction.explanation,
      }
    })

    return (
      <ResultScreen
        score={result.score}
        maxScore={result.max_score}
        accuracy={result.accuracy}
        durationMs={runDurationMs}
        xpEarned={result.xp_earned}
        items={items}
        onExit={onExit}
        onReportItem={handleReportItem}
      />
    )
  }

  if (!template || !currentItem) return null

  const Round = template.Round

  return (
    <div className="flex flex-col gap-3">
      <GameHud
        mode={template.mode}
        durationMs={template.durationMs}
        running={phase === 'playing' || phase === 'confirm_abandon'}
        score={liveScore}
        currentRound={roundIndex + 1}
        totalRounds={totalRounds}
        onTimeUp={handleTimeUp}
      />

      {phase === 'confirm_abandon' ? (
        <div
          role="alertdialog"
          aria-label={t('practice.shell.abandon_title')}
          className="flex flex-col gap-3 rounded-2xl bg-surface-low p-4"
        >
          <p className="text-sm font-medium text-on-surface">{t('practice.shell.abandon_title')}</p>
          <p className="text-xs text-on-surface-variant">{t('practice.shell.abandon_body')}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setPhase('abandoned')
                onAbandon?.()
              }}
              className="min-h-[44px] flex-1 rounded-full bg-error px-4 text-sm font-semibold text-white"
            >
              {t('practice.shell.abandon_confirm')}
            </button>
            <button
              type="button"
              onClick={() => setPhase('playing')}
              className="min-h-[44px] flex-1 rounded-full bg-surface-container px-4 text-sm font-medium text-on-surface"
            >
              {t('practice.shell.abandon_cancel')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <RoundErrorBoundary
            fallback={
              <p role="alert" className="rounded-2xl bg-error/10 p-4 text-sm text-error">
                {t('practice.shell.round_load_error')}
              </p>
            }
          >
            <Suspense
              fallback={
                <p role="status" aria-live="polite" className="rounded-2xl bg-surface-low p-4 text-sm text-on-surface-variant">
                  {t('practice.shell.round_loading')}
                </p>
              }
            >
              <Round item={currentItem} runMode={run.mode} onAnswer={handleAnswer} disabled={isFinishing} />
            </Suspense>
          </RoundErrorBoundary>
          <button
            type="button"
            onClick={() => setPhase('confirm_abandon')}
            className="min-h-[44px] self-start px-1 text-xs font-medium text-on-surface-variant underline"
          >
            {t('practice.shell.abandon_action')}
          </button>
        </>
      )}
    </div>
  )
}
